// Single source of truth for WebP support.
//
// We ship WebP images (much smaller — see scripts/convert-images.js) with a
// transparent PNG/JPG fallback for browsers and TVs that can't decode WebP
// (notably some older Roku builds). Detection runs once, synchronously, at
// import time so the first render already picks the right asset (no flash /
// double-fetch).
//
// Three consumers share this one result:
//   - pickImage(webp, fallback)  -> for <img src>, CSS-in-JS backgroundImage, canvas
//   - <Picture> component         -> native <picture> negotiation for simple <img>
//   - the <html> class (webp / no-webp) -> for SCSS url() fallbacks

function detect() {
    if (typeof document === 'undefined') return false;
    try {
        const canvas = document.createElement('canvas');
        if (!canvas.getContext || !canvas.getContext('2d')) return false;
        // toDataURL returns a data:image/webp URL only when the engine can
        // encode WebP, which in practice tracks decode support across browsers.
        return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) {
        return false;
    }
}

// QA override: force the PNG/JPG fallback path so the same page can be measured
// both ways (WebP vs original) without a rebuild. Add ?nowebp to the URL, or run
// localStorage.setItem('nowebp','1'). Harmless in production.
function forcedOff() {
    try {
        if (typeof window === 'undefined') return false;
        if (/[?&]nowebp\b/i.test(window.location.search)) return true;
        return window.localStorage && window.localStorage.getItem('nowebp') === '1';
    } catch (e) {
        return false;
    }
}

export const supportsWebp = !forcedOff() && detect();

if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.classList.add(supportsWebp ? 'webp' : 'no-webp');
}

// Returns the WebP asset when supported, otherwise the original fallback.
export const pickImage = (webp, fallback) => (supportsWebp && webp ? webp : fallback);

export default pickImage;
